import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  ThumbsUp,
  ThumbsDown,
  BarChart3,
  Users,
  MessageSquare,
  TrendingUp,
  ExternalLink,
} from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import AdminLayout from "@/components/layout/AdminLayout";
import { fetchFeedbackAnalytics } from "@/api/chat";

const TIME_RANGES = [
  { label: "24h", hours: 24 },
  { label: "7d", hours: 168 },
  { label: "30d", hours: 720 },
  { label: "90d", hours: 2160 },
];

export default function AiFeedbackAnalytics() {
  const navigate = useNavigate();
  const [hours, setHours] = useState(168);

  const { data, isLoading, error } = useQuery({
    queryKey: ["feedback-analytics", hours],
    queryFn: () => fetchFeedbackAnalytics(hours),
  });

  if (error) {
    console.error("[AiFeedbackAnalytics] RPC error:", error);
  }

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <AdminLayout title="AI Feedback Analytics">
      {/* Time Range Selector */}
      <div className="flex gap-1 mb-6">
        {TIME_RANGES.map((range) => (
          <Button
            key={range.hours}
            variant={hours === range.hours ? "default" : "outline"}
            size="sm"
            onClick={() => setHours(range.hours)}
          >
            {range.label}
          </Button>
        ))}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-md text-sm text-red-700 dark:text-red-300">
          Error loading analytics: {(error as any)?.message || JSON.stringify(error)}
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
              <BarChart3 className="w-4 h-4" />
              Satisfaction Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">
              {data?.satisfaction_rate != null ? `${data.satisfaction_rate}%` : "-"}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
              <TrendingUp className="w-4 h-4" />
              Feedback Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">
              {data?.feedback_rate != null ? `${data.feedback_rate}%` : "-"}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
              <ThumbsUp className="w-4 h-4 text-green-600" />
              Positive
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-green-600">
              {data?.total_positive ?? "..."}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
              <ThumbsDown className="w-4 h-4 text-red-500" />
              Negative
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-red-500">
              {data?.total_negative ?? "..."}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* By Persona */}
      {data?.by_persona && data.by_persona.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-4 h-4" />
              Satisfaction by Persona
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Persona</TableHead>
                  <TableHead className="text-center">Total Responses</TableHead>
                  <TableHead className="text-center">Rated</TableHead>
                  <TableHead className="text-center">
                    <ThumbsUp className="w-3.5 h-3.5 inline" />
                  </TableHead>
                  <TableHead className="text-center">
                    <ThumbsDown className="w-3.5 h-3.5 inline" />
                  </TableHead>
                  <TableHead className="text-center">Satisfaction</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.by_persona.map((row) => {
                  const rate = row.rated > 0
                    ? Math.round((row.positive / row.rated) * 100)
                    : null;
                  return (
                    <TableRow key={row.persona}>
                      <TableCell>
                        <Badge variant="outline">{row.persona}</Badge>
                      </TableCell>
                      <TableCell className="text-center">{row.total}</TableCell>
                      <TableCell className="text-center">{row.rated}</TableCell>
                      <TableCell className="text-center text-green-600">{row.positive}</TableCell>
                      <TableCell className="text-center text-red-500">{row.negative}</TableCell>
                      <TableCell className="text-center">
                        {rate != null ? (
                          <Badge variant={rate >= 80 ? "default" : rate >= 50 ? "secondary" : "destructive"}>
                            {rate}%
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Daily Trend */}
      {data?.daily_trend && data.daily_trend.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Daily Feedback Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-1 items-end" style={{ height: 128 }}>
              {data.daily_trend.map((day) => {
                const total = day.positive + day.negative;
                const maxTotal = Math.max(...data.daily_trend.map((d) => d.positive + d.negative), 1);
                const barHeight = Math.max((total / maxTotal) * 112, 4);
                const positivePx = total > 0 ? (day.positive / total) * barHeight : 0;
                const negativePx = barHeight - positivePx;
                return (
                  <div key={day.day} className="flex-1 flex flex-col items-center justify-end gap-1" style={{ height: 128 }}>
                    <div
                      className="w-full rounded-sm overflow-hidden flex flex-col"
                      style={{ height: barHeight }}
                    >
                      <div
                        className="bg-red-400"
                        style={{ height: negativePx }}
                      />
                      <div
                        className="bg-green-500 flex-1"
                      />
                    </div>
                    <span className="text-[9px] text-muted-foreground whitespace-nowrap">
                      {new Date(day.day).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                    </span>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-sm bg-green-500" /> Positive
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-sm bg-red-400" /> Negative
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Negative Feedback */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Recent Negative Feedback
            {data?.recent_negative && data.recent_negative.length > 0 && (
              <Badge variant="destructive" className="ml-1">{data.recent_negative.length}</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="text-center py-8 text-muted-foreground">Loading...</p>
          ) : !data?.recent_negative || data.recent_negative.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">
              No negative feedback in this period.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User Question</TableHead>
                  <TableHead>AI Response</TableHead>
                  <TableHead>Persona</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>When</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.recent_negative.map((item) => (
                  <TableRow key={item.message_id}>
                    <TableCell className="max-w-[200px]">
                      <p className="truncate text-sm">{item.user_question || "-"}</p>
                    </TableCell>
                    <TableCell className="max-w-[250px]">
                      <p className="truncate text-sm text-muted-foreground">
                        {item.assistant_response}
                      </p>
                      {item.feedback_comment && (
                        <p className="text-xs text-red-500 mt-0.5 truncate">
                          "{item.feedback_comment}"
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">{item.persona}</Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {item.user_name || "-"}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {formatDate(item.feedback_at)}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => navigate(`/admin/ask-ai/${item.session_id}`)}
                        title="View session"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
}
